<template>
  <section id="seed-list">
    <v-row class="d-flex justify-center mt-5 mb-8">
      <v-col cols="12" lg="10" md="10">
        <card-widget>
          <template #cardBody>
            <page-header :title="$dictionary.app.seeds.index.title" #default>
              <v-col
                cols="12"
                md="8"
                lg="8"
                class="d-flex align-center justify-end flex-wrap flex-lg-nowrap"
              >
                <debounce-search-text-field
                  id="search-seeds-btn"
                  placeholder="Search Seeds..."
                  prop-to-search="seed_address"
                  :lazy-search="getSeedsList"
                  :no-of-records="seedsParams.per_page"
                  :sort-by="sortConfig.sortBy"
                  :sort-order="sortConfig.sortOrder"
                  @search-value="seedsSearch = $event"
                />

                <v-btn
                  color="secondary"
                  outlined
                  @click="addEditSeed()"
                  class="
                    button
                    add-seed-btn
                    font-weight-bold
                    btn-light-grey-border
                    text--secondary
                  "
                >
                  <v-icon class="pr-3 text--secondary"> mdi-plus </v-icon>
                  Add seed
                </v-btn>
                <div id="seeds-csv-btn" class="ml-4">
                  <csv-button
                    :labels="labels"
                    :data="sortedRecords"
                    fileName="seeds"
                    :fields="csvFields"
                    :loading="isLoading"
                    :color="$appConfig.csvButton.color.secondary"
                  />
                </div>
              </v-col>
            </page-header>

            <v-row class="d-flex justify-center pt-3" no-gutters>
              <v-col cols="12" class="pt-3">
                <seeds-table
                  @fetch-logs="getSeedErrorLogs"
                  :areLogsLoading="areLogsLoading"
                  @edit-seed="addEditSeed"
                  @delete-seed="onDeleteSeed"
                  :headers="headers"
                  :is-loading="isLoading"
                  @sorted-records="updateSortedRecords"
                  :data="seedList"
                  :activeItem="currentSeed"
                  @get-records="getSeedsList"
                  :update-records="updateRecords"
                >
                  <template #expanded-seed="{ headers }" v-if="!areLogsLoading">
                    <template v-if="hasSeedErrorLogs">
                      <td :colspan="headers.length" class="px-0">
                        <template v-for="(error, i) in seedErrors">
                          <v-simple-table
                            :key="`${error.error_message}-${i}`"
                            class="w-100"
                          >
                            <tbody>
                              <tr class="border-light-grey text-left">
                                <td class="text-left">
                                  <div class="d-flex align-center">
                                    <v-icon
                                      class="
                                        material-icons
                                        text-secondary
                                        pl-8
                                        ml-3
                                        pr-2
                                      "
                                    >
                                      subdirectory_arrow_right
                                    </v-icon>
                                    <span
                                      class="text--primary"
                                      v-text="error.error_message"
                                    />
                                  </div>
                                </td>

                                <td colspan="2" class="text-right pr-8">
                                  {{ error.created_at | formatDateToAmPm }}
                                </td>
                                <td></td>
                              </tr>
                            </tbody>
                          </v-simple-table>
                          <v-divider
                            v-if="i < seedErrors.length - 1"
                            :key="error.created_at"
                          />
                        </template>
                      </td>
                    </template>
                    <td class="pl-6" :colspan="headers.length" v-else>
                      <div class="d-flex align-center">
                        <v-icon class="material-icons text-secondary pl-8 pr-2">
                          subdirectory_arrow_right
                        </v-icon>
                        <span> {{ seedErrorMessage }} </span>
                      </div>
                    </td>
                  </template>
                </seeds-table>
              </v-col>
            </v-row>
          </template>
        </card-widget></v-col
      >
    </v-row>

    <add-seed-form
      v-if="isDrawerOpen"
      :is-drawer-open="isDrawerOpen"
      @toogle-drawer="toggleDrawer"
      :seed-data="editSeedData"
      @update-records="updateFetchRecords"
    />

    <delete-seed-dialog
      :dialog="deleteSeedDialog.dialog"
      :sub-title="$dictionary.app.seeds.deleteSeed.subtitle"
      :title="deleteSeedDialog.title"
      @close="setDeleteSeedToDefault"
      @delete="deleteSeed(deleteSeedDialog.seed)"
      :is-loading="isSeedDeleting"
    />
  </section>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from "vuex";
import { SeedsTable } from "@/components/tables";
import { manageSeedsHeaders as headers } from "@/data/table-headers";

import {
  PageHeader,
  DeleteRecordDialog as DeleteSeedDialog,
  DebounceSearchTextField,
} from "@/components/shared";
import { AddSeedForm } from "@/components/forms";
import { CsvButton } from "@/components/widgets";

import { TitleMixin, SharedUtilsMixin } from "@/mixins";
import {
  csvFileLabels,
  defer,
  isResponseCode,
  isType,
  hasProp,
  isEmpty,
} from "@/utils/common.utils";
import { deleteSeed, errorLogs } from "@/services";
import { DEBOUNCE_MILLIS, RESPONSE_CODES } from "@/constants";
import CardWidget from "@/components/widgets/CardWidget.vue";

/**
 * Manage Seeds view
 */
export default {
  name: "ManageSeeds",
  /**
   *  View title
   */
  title: "Manage Seeds",
  /**
   * -------------- Mixins -------------
   */
  mixins: [TitleMixin, SharedUtilsMixin],
  /**
   * -------------- Components -------------
   */
  components: {
    SeedsTable,
    PageHeader,
    CsvButton,
    AddSeedForm,
    DeleteSeedDialog,
    DebounceSearchTextField,
    CardWidget,
  },
  /**
   * ---------------- Data properties ------------------
   */
  data() {
    return {
      isLoading: false,
      updateRecords: false,
      isDrawerOpen: false,
      errorMessage: null,
      hasError: false,
      headers,
      labels: csvFileLabels(headers),
      sortedRecords: [],
      editSeedData: {},
      deleteSeedDialog: {
        dialog: false,
        title: "",
        seed: {},
      },
      isSeedDeleting: false,
      seedsParams: {},
      seedsSearch: "",
      areLogsLoading: false,
      seedErrors: null,
      seedErrorMessage: "",
      currentSeed: {},
    };
  },
  /**
   * ---------------- Computed properties ------------------
   */
  computed: {
    ...mapGetters({
      settings: "settings/seeds",
      seedList: "account/filteredSeeds",
    }),
    /**
     * Seeds listing sorting configuration
     */
    sortConfig() {
      return this.getSortConfig("seedsParams", "seed_address");
    },
    /**
     * Csv fields to be downloaded in the csv file
     */
    csvFields() {
      return Object.keys(this.labels);
    },
    /**
     * Mailbox provider id
     */
    providerId() {
      const { id } = this.$route.params;
      return id;
    },
    /**
     * Detrmines if a seed has error logs or not
     * @type {Boolean}
     */
    hasSeedErrorLogs() {
      return !isEmpty(this.seedErrors);
    },
    /**
     * Accounts page breadcrumbs
     * @type {Array}
     */
    breadcrumbs() {
      return [
        {
          title: this.$dictionary.app.seeds.index.title,
          type: this.$appConfig.breadcrumbs.types.route,
        },
      ];
    },
  },
  /**
   * -------------- Methods -------------
   */
  methods: {
    ...mapActions({
      getSeeds: "account/getSeeds",
      setSnackbar: "ui/setSnackbar",
    }),
    ...mapMutations({
      setSeeds: "account/SET_SEEDS",
      hideSnackbar: "ui/HIDE_SNACKBAR",
    }),
    /**
     * Fetches list of error logs of a seed address
     */
    async getSeedErrorLogs({ value, item }) {
      if (!value) return;
      try {
        this.currentSeed = item;
        this.areLogsLoading = true;

        await this.hideSnackbar(true);
        const { data } = await errorLogs(item.seed_address);

        this.seedErrors = data;
        this.seedErrorMessage = "";
      } catch (error) {
        this.seedErrors = null;
        this.seedErrorMessage = `Error logs not found.`;
      } finally {
        this.areLogsLoading = false;
        this.hideSnackbar(false);
      }
    },
    /**
     * Toogles add or edit seed drawer
     * @listens toogle-drawer
     * @param val Boolean value of isDrawer property which determines opening or closing of seed drawer
     */
    toggleDrawer(val) {
      if (!isType(val, "boolean")) return "";
      this.isDrawerOpen = val;
    },
    /**
     * Sorted records
     * @listens sorted-records
     */
    updateSortedRecords(records) {
      this.sortedRecords = records;
    },
    /**
     * Fetches list of seeds
     */
    async getSeedsList(params = this.seedsParams) {
      try {
        if (this.seedsSearch && !hasProp(params, "q[seed_address_cont]")) {
          params["q[seed_address_cont]"] = this.seedsSearch;
        }
        params["q[mailbox_provider_id_eq]"] = this.providerId;
        this.seedsParams = params;
        this.isLoading = true;
        await this.getSeeds(params);
      } catch (error) {
        this.setSnackbar({
          value: true,
          type: this.$appConfig.snackbar.snackbarTypes.error,
          message: this.$errorMessage(error),
        });
      } finally {
        this.isLoading = false;
      }
    },
    /**
     * Open drawer to add a new seed or edit an existing seed
     * @listens edit-seed
     * @param {Object} seed Seed details
     */
    addEditSeed(seed = null) {
      if (seed) this.editSeedData = seed;
      else this.editSeedData = {};

      this.toggleDrawer(true);
      this.setDeleteSeedToDefault();
    },
    /**
     * Delete seed event handler
     * @param {Object} seed Seed details
     */
    onDeleteSeed(seed) {
      const REPLACER = "<<email>>";
      const { title } = this.$dictionary.app.seeds.deleteSeed;

      // Open delete seed dialog
      this.deleteSeedDialog = {
        dialog: true,
        seed,
        title: title.replace(REPLACER, seed.seed_address),
      };
    },
    /**
     * Sets default value of delete seed dialog
     */
    setDeleteSeedToDefault() {
      this.deleteSeedDialog = { dialog: false, seed: {}, title: "" };
    },
    /**
     * Send delete api call and deletes the seed
     */
    async deleteSeed({ id }) {
      try {
        this.isSeedDeleting = true;
        let message = this.$dictionary.errors.common.msg;
        const { status } = await deleteSeed(id);

        if (isResponseCode(status, RESPONSE_CODES.success)) {
          message = this.$dictionary.app.seeds.deleteSeed.successMessage;
        }

        this.setSnackbar({
          value: true,
          type: this.$appConfig.snackbar.snackbarTypes.success,
          message,
        });
        this.updateFetchRecords();
        this.setDeleteSeedToDefault();
      } catch (error) {
        this.setSnackbar({
          value: true,
          type: this.$appConfig.snackbar.snackbarTypes.error,
          message: this.$errorMessage(error),
        });
      } finally {
        this.isSeedDeleting = false;
      }
    },
    /**
     * Sets updateRecords property in the componen
     */
    updateFetchRecords() {
      this.updateRecords = true;
      defer(() => (this.updateRecords = false), DEBOUNCE_MILLIS);
    },
  },
  /**
  |--------------------------------------------------
  | Destroyed lifecycle hook
  |--------------------------------------------------
  */
  destroyed() {
    this.setSeeds([]);
  },
};
</script>
